import { createListenerMiddleware, createSlice, isAnyOf, PayloadAction } from '@reduxjs/toolkit';
import { isBatchQueryResult } from 'components/query-results/query-results.utils';
import { saveStorageValue } from 'hooks/use-storage/use-storage.utils';
import { createSelector } from 'reselect';
import { QueryHistory, QueryResult, QueryTabs, QueryWorkbenchOptions } from 'types/query-result';
import { uniqueId } from 'utils/unique-id/unique-id';
import { RootState } from './root.types';

export const queryResultName = 'query-result';
export const queryResultStorageKey = 'cb:workbench_';

type CommonActionPayload = {
  databaseId: string;
};

export const defaultQueryResultOptions: QueryWorkbenchOptions = {
  outputSelected: QueryTabs.JSON,
  query: {
    collectTimings: true,
    inferSchemas: true,
    autoFormatQueries: false,
    useCBO: true,
    dontSaveHistory: false,
    maxParallelism: '',
    scanConsistency: 'not_bounded',
    positionalParams: [],
    namedParams: [],
    queryTimeout: '600',
    transactionTimeout: '120',
    queryTimeoutNum: 600,
    transactionTimeoutNum: 120,
  },
  sidebarSelected: 'Index Advice',
};

export const defaultQueryResult: QueryResult = {
  query: '',
  query_as_run: '',
  query_context_bucket_as_run: '',
  query_context_scope_as_run: '',
  status: '',
  elapsedTime: '',
  executionTime: '',
  resultSize: '',
  mutationCount: '',
  warnings: '',
  lastRun: null,
  rtt: '',
  data: [
    {
      'No data to display': "Hit the 'Run' button to run a query.",
    },
  ],
  result: '{"No data to display":"Hit the \'Run\' button to run a query."}',
  advice: null,
  query_context_bucket: '',
  query_context_scope: '',
  busy: false,
  requestID: '',
  truncated: false,
  rawJSON: '',
};

export const getInitialState = (): {
  queryByDatabaseId: {
    [databaseId: string]: QueryHistory;
  };
} => ({
  queryByDatabaseId: {},
});

export const queryResultSlice = createSlice({
  name: queryResultName,
  initialState: getInitialState(),
  reducers: {
    initDatabaseQueries(
      state,
      { payload: { databaseId, queryHistory } }: PayloadAction<CommonActionPayload & { queryHistory: QueryHistory }>
    ) {
      state.queryByDatabaseId[databaseId] = queryHistory;
    },
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    triggerQueryHistorySave(state, { payload: { databaseId } }: PayloadAction<CommonActionPayload>) {
      return state;
    },
    appendQueryResult(state, { payload: { databaseId, queryResult } }: PayloadAction<CommonActionPayload & { queryResult: QueryResult }>) {
      const queryHistory = state.queryByDatabaseId[databaseId];
      // if the query at index 0 is empty, replace it with the new query
      if (
        queryHistory.pastQueries[0].query === '' &&
        queryHistory.pastQueries[0].query_as_run === '' &&
        queryHistory.pastQueries.length === 1
      ) {
        queryHistory.pastQueries[0] = queryResult;
        queryHistory.currentQueryIndex = 0;
      } else {
        queryHistory.pastQueries.push(queryResult);
        queryHistory.currentQueryIndex = queryHistory.pastQueries.length - 1;
      }
    },
    detachQueryResult(state, { payload: { databaseId, queryIndex } }: PayloadAction<CommonActionPayload & { queryIndex: number }>) {
      const queryHistory = state.queryByDatabaseId[databaseId];
      const { pastQueries, currentQueryIndex } = queryHistory;
      const isLastSelected = currentQueryIndex === pastQueries.length - 1;

      if (pastQueries.length !== 1) {
        if (isLastSelected) {
          queryHistory.currentQueryIndex -= 1;
        }
        queryHistory.pastQueries = pastQueries.filter((_, index) => index !== queryIndex);
      } else {
        queryHistory.pastQueries = [defaultQueryResult];
      }
    },
    eraseQueryResultsHistory(state, { payload: { databaseId } }: PayloadAction<CommonActionPayload>) {
      state.queryByDatabaseId[databaseId].pastQueries = [defaultQueryResult];
      state.queryByDatabaseId[databaseId].currentQueryIndex = 0;
    },
    setCurrentQueryIndex(
      state,
      { payload: { databaseId, currentQueryIndex } }: PayloadAction<CommonActionPayload & { currentQueryIndex: number }>
    ) {
      state.queryByDatabaseId[databaseId].currentQueryIndex = currentQueryIndex;
    },
    setQueryResult(
      state,
      {
        payload: { databaseId, queryResult: originalQueryResult, queryIndex },
      }: PayloadAction<
        CommonActionPayload & {
          queryIndex: number;
          queryResult: Partial<QueryResult>;
        }
      >
    ) {
      const hasDataChanged =
        state.queryByDatabaseId[databaseId]?.pastQueries?.[queryIndex]?.busy === true && originalQueryResult.busy === false;

      let queryResult = originalQueryResult;

      const isBatchQuery = isBatchQueryResult(queryResult.data);

      if ((originalQueryResult.data && hasDataChanged) || originalQueryResult.busy) {
        let updatedResult;
        let truncated = false;

        if (isBatchQuery) {
          // Handle batch query
          const maxJsonLength = (1024 * 1024 * 2) / (queryResult.data?.length || 1);
          updatedResult = originalQueryResult.data?.map((batch) => {
            let batchResult = JSON.stringify(batch.batchQueryResult, null, 2);
            if (batchResult.length > maxJsonLength && Array.isArray(batch.batchQueryResult)) {
              let arrayLen = batch.batchQueryResult.length;
              while (batchResult.length > maxJsonLength) {
                arrayLen = Math.trunc(((maxJsonLength * 0.95) / batchResult.length) * arrayLen) - 1;
                batchResult = JSON.stringify(batch.batchQueryResult.slice(0, arrayLen), null, 2);
              }
              truncated = true;
            }
            return {
              batchQuery: batch.batchQuery,
              batchQueryResult: JSON.parse(batchResult),
            };
          });
          updatedResult = JSON.stringify(updatedResult, null, 2);
        } else {
          const maxJsonLength = 1024 * 1024 * 2;
          // Handle non-batch query
          updatedResult = JSON.stringify(originalQueryResult.data, null, 2);
          if (Array.isArray(originalQueryResult.data) && updatedResult.length > maxJsonLength) {
            let arrayLen = originalQueryResult.data.length;
            while (updatedResult.length > maxJsonLength) {
              arrayLen = Math.trunc(((maxJsonLength * 0.95) / updatedResult.length) * arrayLen) - 1;
              updatedResult = JSON.stringify(originalQueryResult.data.slice(0, arrayLen), null, 2);
            }
            truncated = true;
          }
        }

        queryResult = { ...queryResult, result: updatedResult, truncated };
      }

      if (state.queryByDatabaseId[databaseId]?.pastQueries?.length > queryIndex) {
        state.queryByDatabaseId[databaseId].pastQueries[queryIndex] = {
          ...state.queryByDatabaseId[databaseId].pastQueries[queryIndex],
          ...queryResult,
        };
      }
    },
    setQueryOptions(state, { payload: { databaseId, options } }: PayloadAction<CommonActionPayload & { options: QueryWorkbenchOptions }>) {
      state.queryByDatabaseId[databaseId].options = options;
    },

    setQueryText(
      state,
      {
        payload: { databaseId, queryIndex, query },
      }: PayloadAction<
        CommonActionPayload & {
          queryIndex: number;
          query: Pick<QueryResult, 'query' | 'status'>;
        }
      >
    ) {
      const pastQueries = state.queryByDatabaseId[databaseId]?.pastQueries;

      if (pastQueries !== undefined && pastQueries.length > queryIndex) {
        const existingQuery = state.queryByDatabaseId[databaseId]?.pastQueries?.[queryIndex];

        pastQueries[queryIndex] = {
          ...existingQuery,
          ...query,
          query: query.query || '',
        };
      }
    },
    setQueryTextStatus(
      state,
      {
        payload: { databaseId, queryIndex, queryStatus },
      }: PayloadAction<
        CommonActionPayload & {
          queryIndex: number;
          queryStatus: string;
        }
      >
    ) {
      state.queryByDatabaseId[databaseId].pastQueries[queryIndex].status = queryStatus;
    },
  },
});

export default queryResultSlice.reducer;
export const {
  setCurrentQueryIndex,
  appendQueryResult,
  setQueryResult,
  setQueryOptions,
  initDatabaseQueries,
  detachQueryResult,
  eraseQueryResultsHistory,
  triggerQueryHistorySave,

  setQueryText,
  setQueryTextStatus,
} = queryResultSlice.actions;

export const getDatabaseQueries = (state: RootState) => state[queryResultName].queryByDatabaseId;
export const getDatabaseQueriesMemo = createSelector([getDatabaseQueries], (query) => {
  return { ...query };
});

let saveHistoryDebounceTimeout: NodeJS.Timeout;

export const queryResultMiddleware = createListenerMiddleware();
queryResultMiddleware.startListening({
  matcher: isAnyOf(
    setCurrentQueryIndex,
    appendQueryResult,
    setQueryResult,
    setQueryOptions,
    detachQueryResult,
    eraseQueryResultsHistory,
    triggerQueryHistorySave,

    setQueryText,
    setQueryTextStatus
  ),
  effect: (action, listenerApi) => {
    clearTimeout(saveHistoryDebounceTimeout);
    // Don't block the main thread with this expensive operation.
    saveHistoryDebounceTimeout = setTimeout(() => {
      const currentState = listenerApi.getState() as RootState;
      const { databaseId } = (action as PayloadAction<CommonActionPayload>).payload;
      const queryHistoryByDatabase = currentState[queryResultName].queryByDatabaseId[databaseId];

      const { dontSaveHistory } = queryHistoryByDatabase.options.query;

      const { pastQueries } = queryHistoryByDatabase;

      const queryHistoryToSave = dontSaveHistory
        ? {
            currentQueryIndex: 0,
            pastQueries: [{ query: '', requestID: uniqueId(), advice: null }],
            options: {
              outputSelected: 'JSON',
              query: queryHistoryByDatabase.options.query,
              sidebarSelected: 'History',
            },
          }
        : {
            ...queryHistoryByDatabase,
            pastQueries: pastQueries.map((query) => ({
              ...query,
              warningsArray: [],
              // don't save the data and result in local storage
              data: undefined,
              result: undefined,
              planResults: undefined,
              planTextResults: undefined,
              isAdviceExecuting: undefined,
              rawJSON: undefined,
              // re-set the status of the truncation
              truncated: undefined,
            })),
          };

      try {
        if (pastQueries.length) {
          saveStorageValue({
            storageKey: `${queryResultStorageKey}${databaseId}`,
            value: queryHistoryToSave,
          });
        } else {
          // eslint-disable-next-line no-console
          console.error('listenerMiddleware/error: tried saving invalid query history, with no past queries');
        }
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error('listenerMiddleware/error', e);
      }
    }, 0);
  },
});
